import React, { useState, useEffect, useCallback } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import oneStopService from '../services/oneStopService';

const OneStop = () => {
  const [treeData, setTreeData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [leftPaneWidth, setLeftPaneWidth] = useState(350);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    loadTreeData();
  }, []);

  const loadTreeData = async () => {
    try {
      const response = await oneStopService.getTreeStructure();
      setTreeData(response.data);

      // Auto-expand first level nodes for better UX
      const firstLevelNodes = new Set();
      response.data.forEach(node => {
        if (node.children && node.children.length > 0) {
          firstLevelNodes.add(node.id);
        }
      });
      setExpandedNodes(firstLevelNodes);

      setLoading(false);
    } catch (error) {
      console.error('Error loading tree data:', error);
      setError('Failed to load tree data');
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const findNodePath = (nodes, targetId, path = []) => {
    for (const node of nodes) {
      const currentPath = [...path, node];

      if (node.id === targetId) {
        return currentPath;
      }

      if (node.children && node.children.length > 0) {
        const foundPath = findNodePath(node.children, targetId, currentPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }
    return null;
  };

  const selectNode = async (node) => {
    setSelectedNode(node);

    // If node has URL, you could optionally load additional content
    if (node.url) {
      // Handle URL navigation or content loading
      console.log('Node URL:', node.url);
    }
  };

  const handleMouseDown = useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    
    const containerRect = document.querySelector('.split-pane-container').getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    
    // Set minimum and maximum widths
    if (newWidth >= 250 && newWidth <= containerRect.width - 300) {
      setLeftPaneWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const renderTreeNode = (node, level = 0, isLast = false, parentPath = []) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode && selectedNode.id === node.id;

    return (
      <div key={node.id} className={`thread-node level-${level} ${isLast ? 'last-child' : ''}`}>
        {/* Thread line structure */}
        <div className="thread-structure">
          {/* Vertical thread lines for parent levels */}
          {parentPath.map((hasMore, index) => (
            <div
              key={index}
              className={`thread-vertical ${hasMore ? 'has-siblings' : 'no-siblings'}`}
              style={{ left: `${index * 24}px` }}
            />
          ))}

          {/* Current level connector */}
          {level > 0 && (
            <div
              className={`thread-connector ${isLast ? 'last' : 'middle'}`}
              style={{ left: `${(level - 1) * 24}px` }}
            />
          )}
        </div>

        {/* Node content hanging from the thread */}
        <div className={`thread-node-wrapper level-${level}`}>
          <div
            className={`thread-node-content ${isSelected ? 'selected' : ''} ${hasChildren ? 'has-children' : 'leaf-node'}`}
            onClick={() => selectNode(node)}
          >
            {/* Expand/Collapse indicator */}
            <div className="thread-toggle-area">
              {hasChildren && (
                <button
                  className="thread-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(node.id);
                  }}
                >
                  <div className={`thread-toggle-icon ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="toggle-line horizontal"></div>
                    <div className={`toggle-line vertical ${isExpanded ? 'hidden' : ''}`}></div>
                  </div>
                </button>
              )}
              {!hasChildren && (
                <div className="thread-leaf-dot"></div>
              )}
            </div>

            {/* Node content */}
            <div className="thread-node-body">
              {/* Node icon */}
              <div className="thread-node-icon">
                {node.icon ? (
                  <i className={`${node.icon} ${hasChildren ? 'folder-icon' : 'file-icon'}`}></i>
                ) : (
                  <i className={`fas ${hasChildren ? 'fa-folder' : 'fa-file-alt'} ${hasChildren ? 'folder-icon' : 'file-icon'}`}></i>
                )}
              </div>

              {/* Node information */}
              <div className="thread-node-info">
                <div className="thread-node-title">{node.title}</div>
                {node.description && (
                  <div className="thread-node-subtitle">
                    {node.description.length > 60
                      ? `${node.description.substring(0, 60)}...`
                      : node.description
                    }
                  </div>
                )}
                {hasChildren && (
                  <div className="thread-node-count">
                    <i className="fas fa-layer-group"></i>
                    <span>{node.children.length} item{node.children.length !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>

              {/* Node actions */}
              <div className="thread-node-actions">
                {node.url && (
                  <button
                    className="thread-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(node.url, '_blank');
                    }}
                    title="Open external link"
                  >
                    <i className="fas fa-external-link-alt"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Children hanging from this node */}
        {hasChildren && isExpanded && (
          <div className="thread-children">
            {node.children
              .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
              .map((child, index, array) => {
                const childIsLast = index === array.length - 1;
                const newParentPath = [...parentPath, !childIsLast];
                return renderTreeNode(child, level + 1, childIsLast, newParentPath);
              })
            }
          </div>
        )}
      </div>
    );
  };

  const renderNodeDetails = () => {
    if (!selectedNode) {
      return (
        <div className="content-empty-state">
          <div className="empty-content">
            <div className="empty-icon">
              <i className="fas fa-mouse-pointer"></i>
            </div>
            <h3 className="empty-title">Select an Item</h3>
            <p className="empty-description">
              Click on any item from the navigation tree to view its content and details here.
            </p>
            <div className="empty-tips">
              <div className="tip">
                <i className="fas fa-plus-circle text-primary me-2"></i>
                <span>Click the + button to expand folders</span>
              </div>
              <div className="tip">
                <i className="fas fa-external-link-alt text-success me-2"></i>
                <span>Items with links will show a link button</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const hasChildren = selectedNode.children && selectedNode.children.length > 0;
    const nodeType = hasChildren ? 'Container' : 'Item';
    const nodeIcon = selectedNode.icon || (hasChildren ? 'fas fa-folder' : 'fas fa-file-alt');

    return (
      <div className="content-viewer">
        {/* Content Header */}
        <div className="content-header">
          <div className="content-header-main">
            <div className="content-icon">
              <i className={`${nodeIcon}`}></i>
            </div>
            <div className="content-title-section">
              <h2 className="content-title">{selectedNode.title}</h2>
              <div className="content-meta">
                <span className="content-type">
                  <i className="fas fa-tag me-1"></i>
                  {nodeType}
                </span>
                {selectedNode.url && (
                  <span className="content-link">
                    <i className="fas fa-external-link-alt me-1"></i>
                    Has external link
                  </span>
                )}
              </div>
            </div>
          </div>

          {selectedNode.url && (
            <div className="content-actions">
              <Button
                variant="primary"
                href={selectedNode.url}
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center"
              >
                <i className="fas fa-external-link-alt me-2"></i>
                Open Link
              </Button>
            </div>
          )}
        </div>

        {/* Content Body */}
        <div className="content-body">
          {/* Breadcrumb Navigation */}
          {(() => {
            const nodePath = findNodePath(treeData, selectedNode.id);
            return nodePath && nodePath.length > 1 ? (
              <div className="p-3 bg-light border-bottom">
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-route text-secondary me-2"></i>
                  <small className="text-secondary font-weight-bold">NAVIGATION PATH</small>
                </div>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0">
                    {nodePath.map((pathNode, index) => (
                      <li
                        key={pathNode.id}
                        className={`breadcrumb-item ${index === nodePath.length - 1 ? 'active' : ''}`}
                      >
                        {index === nodePath.length - 1 ? (
                          <span className="text-primary font-weight-bold">
                            <i className={pathNode.icon || 'fas fa-file-alt'}></i>
                            <span className="ms-1">{pathNode.title}</span>
                          </span>
                        ) : (
                          <button
                            className="btn btn-link p-0 text-decoration-none"
                            onClick={() => selectNode(pathNode)}
                          >
                            <i className={pathNode.icon || 'fas fa-folder'}></i>
                            <span className="ms-1">{pathNode.title}</span>
                          </button>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>
            ) : null;
          })()}

          {/* Description Section */}
          {selectedNode.description && (
            <div className="p-4 border-bottom">
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-align-left text-primary me-2"></i>
                <h6 className="mb-0 text-primary">Description</h6>
              </div>
              <div
                className="node-description"
                dangerouslySetInnerHTML={{
                  __html: selectedNode.description.replace(/\n/g, '<br/>')
                }}
              />
            </div>
          )}

          {/* Children Overview */}
          {hasChildren && (
            <div className="p-4 border-bottom bg-light">
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-layer-group text-info me-2"></i>
                <h6 className="mb-0 text-info">Child Items</h6>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="stat-card">
                    <div className="stat-number">{selectedNode.children.length}</div>
                    <div className="stat-label">Total Items</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="stat-card">
                    <div className="stat-number">
                      {selectedNode.children.filter(child => child.children && child.children.length > 0).length}
                    </div>
                    <div className="stat-label">Containers</div>
                  </div>
                </div>
              </div>

              {/* Quick child preview */}
              <div className="mt-3">
                <small className="text-muted d-block mb-2">Quick Preview:</small>
                <div className="child-preview">
                  {selectedNode.children.slice(0, 3).map((child, index) => (
                    <div key={child.id} className="child-preview-item">
                      <i className={child.icon || 'fas fa-file-alt'}></i>
                      <span>{child.title}</span>
                    </div>
                  ))}
                  {selectedNode.children.length > 3 && (
                    <div className="child-preview-item text-muted">
                      <i className="fas fa-ellipsis-h"></i>
                      <span>+{selectedNode.children.length - 3} more</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions Section */}
          {selectedNode.url && (
            <div className="p-4 border-bottom">
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-external-link-alt text-success me-2"></i>
                <h6 className="mb-0 text-success">External Link</h6>
              </div>
              <Button
                variant="outline-success"
                href={selectedNode.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-100"
              >
                <i className="fas fa-external-link-alt me-2"></i>
                Open External Link
              </Button>
            </div>
          )}

          {/* Metadata Section */}
          <div className="node-metadata p-4">
            <div className="d-flex align-items-center mb-3">
              <i className="fas fa-info-circle text-secondary me-2"></i>
              <h6 className="mb-0 text-secondary">Node Information</h6>
            </div>

            <div className="metadata-grid">
              <div className="metadata-item">
                <div className="metadata-label">Node ID</div>
                <div className="metadata-value">
                  <code>{selectedNode.id}</code>
                </div>
              </div>

              <div className="metadata-item">
                <div className="metadata-label">Display Order</div>
                <div className="metadata-value">
                  <span className="badge bg-secondary">{selectedNode.displayOrder || 0}</span>
                </div>
              </div>

              <div className="metadata-item">
                <div className="metadata-label">Node Type</div>
                <div className="metadata-value">
                  <span className={`badge ${hasChildren ? 'bg-primary' : 'bg-success'}`}>
                    <i className={`${hasChildren ? 'fas fa-folder' : 'fas fa-file-alt'} me-1`}></i>
                    {nodeType}
                  </span>
                </div>
              </div>

              {hasChildren && (
                <div className="metadata-item">
                  <div className="metadata-label">Children Count</div>
                  <div className="metadata-value">
                    <span className="badge bg-info">{selectedNode.children.length}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <h4 className="mt-3">Loading OneStop...</h4>
      </Container>
    );
  }

  return (
    <div className="onestop-page">
      {/* Header */}
      <div className="onestop-header">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between py-3">
            <h1 className="mb-0">
              <i className="fas fa-sitemap me-2 text-primary"></i>
              OneStop Navigation
            </h1>
            <div className="header-actions">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={loadTreeData}
                disabled={loading}
              >
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </Button>
            </div>
          </div>
          {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
        </Container>
      </div>

      {/* Split Pane Container */}
      <div className="split-pane-container">
        {/* Left Pane - Navigation Tree */}
        <div
          className="split-pane-left"
          style={{ width: `${leftPaneWidth}px` }}
        >
          <div className="pane-header">
            <div className="pane-title">
              <i className="fas fa-sitemap me-2"></i>
              Navigation Tree
            </div>
            <div className="pane-subtitle">
              {treeData.length} root item{treeData.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="pane-content">
            {loading ? (
              <div className="loading-state">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading tree...</span>
              </div>
            ) : treeData.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-folder-open fa-2x mb-3 text-muted"></i>
                <p className="text-muted mb-0">No navigation items available</p>
              </div>
            ) : (
              <div className="tree-container">
                {treeData.map((node, index, array) => {
                  const isLast = index === array.length - 1;
                  return renderTreeNode(node, 0, isLast, []);
                })}
              </div>
            )}
          </div>
        </div>

        {/* Resizer */}
        <div
          className={`split-pane-resizer ${isResizing ? 'resizing' : ''}`}
          onMouseDown={handleMouseDown}
        >
          <div className="resizer-handle">
            <div className="resizer-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        </div>

        {/* Right Pane - Content */}
        <div className="split-pane-right">
          <div className="pane-header">
            <div className="pane-title">
              <i className="fas fa-file-alt me-2"></i>
              {selectedNode ? selectedNode.title : 'Content Viewer'}
            </div>
            {selectedNode && (
              <div className="pane-subtitle">
                {selectedNode.children && selectedNode.children.length > 0
                  ? `Container with ${selectedNode.children.length} items`
                  : 'Content item'
                }
              </div>
            )}
          </div>

          <div className="pane-content">
            {renderNodeDetails()}
          </div>
        </div>
      </div>

      <style jsx>{`
        /* OneStop Page Layout */
        .onestop-page {
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: #f8f9fa;
        }
        
        .onestop-header {
          background: #ffffff;
          border-bottom: 1px solid #dee2e6;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          z-index: 10;
        }
        
        .header-actions {
          display: flex;
          gap: 8px;
        }
        
        /* Split Pane Layout */
        .split-pane-container {
          flex: 1;
          display: flex;
          overflow: hidden;
          position: relative;
        }
        
        .split-pane-left {
          background: #ffffff;
          border-right: 1px solid #dee2e6;
          display: flex;
          flex-direction: column;
          min-width: 250px;
          max-width: 60%;
        }
        
        .split-pane-right {
          flex: 1;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          min-width: 300px;
        }
        
        /* Resizer */
        .split-pane-resizer {
          width: 6px;
          background: #f8f9fa;
          cursor: col-resize;
          display: flex;
          align-items: center;
          justify-content: center;
          border-left: 1px solid #dee2e6;
          border-right: 1px solid #dee2e6;
          transition: background-color 0.2s ease;
          position: relative;
        }
        
        .split-pane-resizer:hover,
        .split-pane-resizer.resizing {
          background: #e9ecef;
        }
        
        .resizer-handle {
          height: 40px;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .resizer-dots {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .resizer-dots .dot {
          width: 3px;
          height: 3px;
          background: #6c757d;
          border-radius: 50%;
          opacity: 0.5;
        }
        
        .split-pane-resizer:hover .dot {
          opacity: 0.8;
        }
        
        /* Pane Headers */
        .pane-header {
          padding: 16px 20px;
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          flex-shrink: 0;
        }
        
        .pane-title {
          font-size: 16px;
          font-weight: 600;
          color: #212529;
          margin-bottom: 4px;
        }
        
        .pane-subtitle {
          font-size: 12px;
          color: #6c757d;
          font-weight: 500;
        }
        
        /* Pane Content */
        .pane-content {
          flex: 1;
          overflow: auto;
          position: relative;
        }
        
        .loading-state {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #6c757d;
        }
        
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }
        
        /* Tree Container */
        .tree-container {
          padding: 16px 8px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #ffffff;
          min-height: 100%;
        }
        
        /* Thread Structure */
        .thread-node {
          position: relative;
          margin: 0;
        }
        
        .thread-structure {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }
        
        .thread-vertical {
          position: absolute;
          top: 0;
          width: 2px;
          background: #d1d5db;
          height: 100%;
        }
        
        .thread-vertical.has-siblings {
          background: #9ca3af;
        }
        
        .thread-connector {
          position: absolute;
          top: 20px;
          width: 20px;
          height: 2px;
          background: #9ca3af;
        }
        
        .thread-connector.last {
          background: #9ca3af;
        }
        
        .thread-connector::before {
          content: '';
          position: absolute;
          left: 0;
          top: -20px;
          width: 2px;
          height: 20px;
          background: #9ca3af;
        }
        
        .thread-connector.last::after {
          content: '';
          position: absolute;
          right: -2px;
          top: 0;
          width: 4px;
          height: 4px;
          background: #6b7280;
          border-radius: 50%;
        }
        
        /* Node Wrapper */
        .thread-node-wrapper {
          position: relative;
          z-index: 2;
          margin-left: 0;
          margin-bottom: 8px;
        }
        
        .thread-node-wrapper.level-0 {
          margin-left: 0;
        }
        
        .thread-node-wrapper.level-1 {
          margin-left: 24px;
        }
        
        .thread-node-wrapper.level-2 {
          margin-left: 48px;
        }
        
        .thread-node-wrapper.level-3 {
          margin-left: 72px;
        }
        
        .thread-node-wrapper.level-4 {
          margin-left: 96px;
        }
        
        /* Node Content */
        .thread-node-content {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 12px;
          position: relative;
        }
        
        .thread-node-content:hover {
          background: #f9fafb;
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59,130,246,0.15);
          transform: translateY(-1px);
        }
        
        .thread-node-content.selected {
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          border-color: #3b82f6;
          box-shadow: 0 4px 16px rgba(59,130,246,0.25);
        }
        
        .thread-node-content.has-children {
          border-left: 4px solid #3b82f6;
        }
        
        .thread-node-content.leaf-node {
          border-left: 4px solid #10b981;
        }
        
        /* Toggle Area */
        .thread-toggle-area {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 2px;
        }
        
        .thread-toggle {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 2px;
          transition: all 0.2s ease;
        }
        
        .thread-toggle:hover {
          background: #f3f4f6;
        }
        
        .thread-toggle-icon {
          position: relative;
          width: 12px;
          height: 12px;
        }
        
        .toggle-line {
          position: absolute;
          background: #6b7280;
          transition: all 0.2s ease;
        }
        
        .toggle-line.horizontal {
          top: 50%;
          left: 2px;
          right: 2px;
          height: 2px;
          transform: translateY(-50%);
        }
        
        .toggle-line.vertical {
          left: 50%;
          top: 2px;
          bottom: 2px;
          width: 2px;
          transform: translateX(-50%);
        }
        
        .toggle-line.vertical.hidden {
          opacity: 0;
          transform: translateX(-50%) rotate(90deg);
        }
        
        .thread-leaf-dot {
          width: 6px;
          height: 6px;
          background: #10b981;
          border-radius: 50%;
          opacity: 0.7;
        }
        
        /* Node Body */
        .thread-node-body {
          flex: 1;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }
        
        .thread-node-icon {
          flex-shrink: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          margin-top: 2px;
        }
        
        .folder-icon {
          color: #f59e0b;
        }
        
        .file-icon {
          color: #6b7280;
        }
        
        .thread-node-info {
          flex: 1;
          min-width: 0;
        }
        
        .thread-node-title {
          font-weight: 600;
          color: #111827;
          font-size: 14px;
          line-height: 1.4;
          margin: 0 0 4px 0;
        }
        
        .thread-node-content.selected .thread-node-title {
          color: #1d4ed8;
        }
        
        .thread-node-subtitle {
          font-size: 12px;
          color: #6b7280;
          line-height: 1.4;
          margin-bottom: 4px;
        }
        
        .thread-node-count {
          font-size: 11px;
          color: #3b82f6;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .thread-node-count i {
          font-size: 10px;
        }
        
        /* Node Actions */
        .thread-node-actions {
          flex-shrink: 0;
          opacity: 0;
          transition: opacity 0.2s ease;
          margin-top: 2px;
        }
        
        .thread-node-content:hover .thread-node-actions {
          opacity: 1;
        }
        
        .thread-action-btn {
          background: none;
          border: 1px solid #d1d5db;
          padding: 4px 6px;
          font-size: 11px;
          border-radius: 4px;
          cursor: pointer;
          color: #6b7280;
          transition: all 0.2s ease;
        }
        
        .thread-action-btn:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
          color: #374151;
        }
        
        /* Children */
        .thread-children {
          margin-top: 4px;
        }
        
        /* Level-specific styling */
        .level-0 .thread-node-content {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border: 2px solid #3b82f6;
          box-shadow: 0 2px 8px rgba(59,130,246,0.1);
        }
        
        .level-0 .thread-node-title {
          font-size: 16px;
          font-weight: 700;
        }
        
        .level-1 .thread-node-content {
          border-left-color: #06b6d4;
        }
        
        .level-2 .thread-node-content {
          border-left-color: #10b981;
        }
        
        .level-3 .thread-node-content {
          border-left-color: #f59e0b;
        }
        
        .level-4 .thread-node-content {
          border-left-color: #ef4444;
        }
        
        .node-description {
          line-height: 1.6;
        }
        
        .node-metadata {
          border-top: 1px solid #dee2e6;
          padding-top: 1rem;
          margin-top: 1rem;
          background: #f8f9fa;
          border-radius: 6px;
          padding: 12px;
        }
        
        /* Scrollbar styling */
        .card-body::-webkit-scrollbar {
          width: 8px;
        }
        
        .card-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .card-body::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .card-body::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        /* Animation for expanding/collapsing */
        .tree-children-container {
          animation: slideDown 0.3s ease-out;
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Node Details Panel Styles */
        .bg-gradient-primary {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        }
        
        .node-header-icon {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.2);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .stat-card {
          text-align: center;
          padding: 12px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .stat-number {
          font-size: 24px;
          font-weight: bold;
          color: #007bff;
          line-height: 1;
        }
        
        .stat-label {
          font-size: 12px;
          color: #6c757d;
          margin-top: 4px;
        }
        
        .child-preview {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .child-preview-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 8px;
          background: white;
          border-radius: 4px;
          font-size: 12px;
        }
        
        .child-preview-item i {
          width: 16px;
          color: #6c757d;
        }
        
        .metadata-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        
        .metadata-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .metadata-label {
          font-size: 12px;
          font-weight: 600;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .metadata-value {
          font-size: 14px;
          color: #212529;
        }
        
        .metadata-value code {
          background: #f8f9fa;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 12px;
          color: #e83e8c;
        }
        
        /* Breadcrumb Styles */
        .breadcrumb {
          background: none;
          padding: 0;
          margin: 0;
        }
        
        .breadcrumb-item {
          font-size: 13px;
        }
        
        .breadcrumb-item button {
          color: #6c757d;
          font-size: 13px;
          border: none;
          background: none;
        }
        
        .breadcrumb-item button:hover {
          color: #007bff;
          text-decoration: underline !important;
        }
        
        .breadcrumb-item.active {
          color: #007bff;
        }
        
        .breadcrumb-item + .breadcrumb-item::before {
          content: "›";
          color: #6c757d;
          font-weight: bold;
        }
        
        /* Content Viewer Styles */
        .content-empty-state {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 60px 40px;
        }
        
        .empty-content {
          text-align: center;
          max-width: 400px;
        }
        
        .empty-icon {
          font-size: 64px;
          color: #dee2e6;
          margin-bottom: 24px;
        }
        
        .empty-title {
          font-size: 24px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 16px;
        }
        
        .empty-description {
          font-size: 16px;
          color: #6c757d;
          line-height: 1.5;
          margin-bottom: 32px;
        }
        
        .empty-tips {
          display: flex;
          flex-direction: column;
          gap: 12px;
          text-align: left;
        }
        
        .tip {
          display: flex;
          align-items: center;
          font-size: 14px;
          color: #6c757d;
        }
        
        /* Content Viewer */
        .content-viewer {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: #ffffff;
        }
        
        .content-header {
          padding: 24px 32px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 1px solid #dee2e6;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        
        .content-header-main {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        
        .content-icon {
          width: 48px;
          height: 48px;
          background: #ffffff;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: #495057;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .content-title-section {
          flex: 1;
        }
        
        .content-title {
          font-size: 28px;
          font-weight: 700;
          color: #212529;
          margin: 0 0 8px 0;
          line-height: 1.2;
        }
        
        .content-meta {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .content-type {
          font-size: 14px;
          color: #6c757d;
          font-weight: 500;
        }
        
        .content-link {
          font-size: 14px;
          color: #28a745;
          font-weight: 500;
        }
        
        .content-actions {
          flex-shrink: 0;
        }
        
        .content-body {
          flex: 1;
          overflow: auto;
          padding: 0;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .split-pane-container {
            flex-direction: column;
          }
          
          .split-pane-left {
            width: 100% !important;
            max-height: 40vh;
            border-right: none;
            border-bottom: 1px solid #dee2e6;
          }
          
          .split-pane-resizer {
            display: none;
          }
          
          .split-pane-right {
            min-width: auto;
          }
          
          .content-header {
            padding: 16px 20px;
          }
          
          .content-header-main {
            gap: 12px;
          }
          
          .content-icon {
            width: 40px;
            height: 40px;
            font-size: 20px;
          }
          
          .content-title {
            font-size: 20px;
          }
          
          .metadata-grid {
            grid-template-columns: 1fr;
          }
          
          .tree-node-inner {
            padding: 8px 12px;
            gap: 8px;
          }
          
          .child-node {
            margin-left: 16px;
          }
          
          .child-node::before {
            left: -16px;
            width: 12px;
          }
          
          .child-node:not(:last-child)::after {
            left: -16px;
          }
        }
      `}</style>
    </div>
  );
};

export default OneStop;